// Authentication.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Authentication() {
  const [code, setCode] = useState("");
  const [validCodes, setValidCodes] = useState([]);
  const [status, setStatus] = useState(""); // success | error | warning | exhausted
  const [isLoading, setIsLoading] = useState(false);
  const [recentCodes, setRecentCodes] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [usedCodesCount, setUsedCodesCount] = useState({}); // Track how many times each code is used

  const MAX_ATTEMPTS = 6; // Maximum 6 attempts per code

  useEffect(() => {
    setIsVisible(true);
    loadAuthCodes();
    loadRecentCodes();
    loadUsedCodesCount();

    // set the pointer to top of the page
    window.scrollTo(0, 0);
  }, []);

  // Load used codes count from localStorage
  const loadUsedCodesCount = () => {
    const saved = localStorage.getItem("usedCodesCount");
    if (saved) {
      setUsedCodesCount(JSON.parse(saved));
    }
  };

  // Save used codes count to localStorage
  const saveUsedCodesCount = (code) => {
    const updatedCount = {
      ...usedCodesCount,
      [code]: (usedCodesCount[code] || 0) + 1
    };
    setUsedCodesCount(updatedCount);
    localStorage.setItem("usedCodesCount", JSON.stringify(updatedCount));
  };

  // Load authentication codes from text file
  const loadAuthCodes = async () => {
    try {
      const response = await fetch("/auth-codes.txt");
      const text = await response.text();
      
      console.log("Raw text from file:", text.substring(0, 200));
      
      let codesArray = [];
      
      try {
        let cleanText = text.trim();
        
        if (cleanText.startsWith('[') && cleanText.endsWith(']')) {
          codesArray = JSON.parse(cleanText);
          console.log("Parsed as JSON array, count:", codesArray.length);
        } else {
          codesArray = cleanText
            .split(',')
            .map(code => code.trim().replace(/["\[\]]/g, ''))
            .filter(code => code.length > 0);
          console.log("Parsed by comma split, count:", codesArray.length);
        }
      } catch (parseError) {
        console.error("JSON parse failed, trying alternative method:", parseError);
        const regex = /"([A-Z0-9]+)"/g;
        const matches = [...text.matchAll(regex)];
        codesArray = matches.map(match => match[1]);
        console.log("Extracted using regex, count:", codesArray.length);
      }
      
      const finalCodes = codesArray.map(code => code.trim().toUpperCase());
      setValidCodes(finalCodes);
      console.log(`✅ Loaded ${finalCodes.length} authentication codes`);
      console.log("Sample codes:", finalCodes.slice(0, 10));
      
    } catch (err) {
      console.error("Failed to load auth codes", err);
      setValidCodes([
        "TGE286", "WOC804", "XCD443", "JYR142", "GQA780"
      ].map(c => c.toUpperCase()));
    }
  };

  const loadRecentCodes = () => {
    const saved = localStorage.getItem("recentAuthCodes");
    if (saved) {
      setRecentCodes(JSON.parse(saved));
    } else {
      setRecentCodes([]);
    }
  };

  const saveRecentCode = (code, status, usageCount) => {
    const newEntry = {
      code: code,
      date: new Date().toISOString().split('T')[0],
      status: status,
      attempts: usageCount // Track how many times used
    };
    const updated = [newEntry, ...recentCodes.slice(0, 4)];
    setRecentCodes(updated);
    localStorage.setItem("recentAuthCodes", JSON.stringify(updated));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setStatus("error");
      return;
    }

    setIsLoading(true);
    const enteredCode = code.trim().toUpperCase();
    
    console.log("Entered code:", enteredCode);
    console.log("Valid codes count:", validCodes.length);
    
    // Check if code exists in valid codes
    const isValidCode = validCodes.includes(enteredCode);
    
    // Get current usage count for this code
    const currentUsageCount = usedCodesCount[enteredCode] || 0;
    console.log(`Code ${enteredCode} has been used ${currentUsageCount} times (max ${MAX_ATTEMPTS})`);
    
    // Check if code has been used MAX_ATTEMPTS or more times
    const isExhausted = currentUsageCount >= MAX_ATTEMPTS;
    
    // Calculate remaining attempts
    const remainingAttempts = MAX_ATTEMPTS - currentUsageCount;

    setTimeout(() => {
      if (isValidCode && !isExhausted) {
        // Valid and not exhausted - SUCCESS
        const newUsageCount = currentUsageCount + 1;
        const newRemainingAttempts = MAX_ATTEMPTS - newUsageCount;
        
        setStatus("success");
        saveRecentCode(enteredCode, "valid", newUsageCount);
        saveUsedCodesCount(enteredCode);
        
        // Show warning if this was the last attempt (after using, remaining becomes 0)
        if (newRemainingAttempts === 0) {
          setTimeout(() => {
            setStatus("warning");
          }, 2000);
        }
      } 
      else if (isValidCode && isExhausted) {
        // Valid but exhausted (used MAX_ATTEMPTS times) - INVALID now
        setStatus("exhausted");
        saveRecentCode(enteredCode, "exhausted", currentUsageCount);
      }
      else {
        // Invalid code
        setStatus("error");
        saveRecentCode(enteredCode, "invalid", currentUsageCount);
      }
      
      setIsLoading(false);
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setStatus("");
      }, 3000);
    }, 800);
  };

  // Get usage count for display
  const getCodeUsageCount = (code) => {
    return usedCodesCount[code] || 0;
  };

  // Get remaining attempts for display
  const getRemainingAttempts = (code) => {
    const used = usedCodesCount[code] || 0;
    return Math.max(0, MAX_ATTEMPTS - used);
  };

  return (
    <div className="authentication-page min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-cyan-600/20 to-blue-600/20 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
          
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex justify-center items-center gap-2 text-sm text-gray-300 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-blue-400">Authentication</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Verify Your 
              </span>
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {" "}Product Authenticity
              </span>
            </h1>
            
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Enter your unique product authentication code to verify its genuineness
            </p>
            
            {/* Stats Badge */}
            <div className="mt-6 inline-block bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm">
              <span className="text-blue-300">✓</span> {validCodes.length}+ Products Registered
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Information */}
          <div className={`lg:col-span-1 space-y-6 transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Secure Verification</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                Each Anabolic Pharma product comes with a unique authentication code. Enter it below to verify 
                its authenticity and ensure you're getting genuine products.
              </p>
              
              <div className="space-y-3">
                {[
                  "Unique codes for each product",
                  "Real-time verification",
                  `Each code can be verified up to ${MAX_ATTEMPTS} times`,
                  "Instant results"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* Info Box about attempts */}
              <div className="mt-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-xs text-yellow-800 dark:text-yellow-400 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Each code can be verified maximum {MAX_ATTEMPTS} times
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Where to find your code?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                The authentication code is located on the label of your product packaging or vial.
              </p>
              <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-500">
                <span className="flex items-center gap-1">📍 On the product label</span>
                <span className="flex items-center gap-1">📦 On the box packaging</span>
              </div>
            </div>
          </div>

          {/* Center Column - Verification Form */}
          <div className={`lg:col-span-1 transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-100 dark:border-gray-700">
              <div className="text-center mb-8">
                <div className="inline-block p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 rounded-full mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verify Authenticity</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Enter your unique product authentication code</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      setStatus("");
                    }}
                    placeholder="Enter authentication code (e.g., JVV738)..."
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-gray-900 dark:text-white font-mono text-sm uppercase"
                    maxLength="30"
                    autoComplete="off"
                  />
                </div>

                {/* Success Message */}
                {status === "success" && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl animate-fade-in">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-green-700 dark:text-green-400 font-semibold">✓ Product verified successfully!</p>
                        <p className="text-green-600 dark:text-green-500 text-sm mt-1">This is a genuine Anabolic Pharma product.</p>
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-600 rounded-full transition-all duration-500"
                                style={{ width: `${(getCodeUsageCount(code) / MAX_ATTEMPTS) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              {getRemainingAttempts(code)}/{MAX_ATTEMPTS} remaining
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning Message - Last attempt used */}
                {status === "warning" && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl animate-fade-in">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-yellow-700 dark:text-yellow-400 font-semibold">⚠️ Last verification used!</p>
                        <p className="text-yellow-600 dark:text-yellow-500 text-sm mt-1">This code has been verified {MAX_ATTEMPTS} times and cannot be used for further verifications.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Exhausted Message - Code used MAX_ATTEMPTS times */}
                {status === "exhausted" && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl animate-fade-in">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-red-700 dark:text-red-400 font-semibold">✗ Code has expired!</p>
                        <p className="text-red-600 dark:text-red-500 text-sm mt-1">This authentication code has already been used {MAX_ATTEMPTS} times and is no longer valid.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Invalid Code Message */}
                {status === "error" && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl animate-fade-in">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-red-700 dark:text-red-400 font-semibold">✗ Invalid authentication code!</p>
                        <p className="text-red-600 dark:text-red-500 text-sm mt-1">Please check the code and try again.</p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Verify Product
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  )}
                </button>

                <p className="text-xs text-gray-400 dark:text-gray-500 text-center flex items-center justify-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Secure verification • Maximum {MAX_ATTEMPTS} verifications per code
                </p>
              </form>
            </div>
          </div>

          {/* Right Column - Recent Verifications */}
          <div className={`lg:col-span-1 transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Recent Verifications
              </h3>

              <div className="space-y-4">
                {recentCodes.length > 0 ? (
                  recentCodes.map((item, index) => {
                    const remainingAttempts = MAX_ATTEMPTS - (item.attempts || 0);
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-700">
                        <div className="flex-1">
                          <p className="text-sm font-mono text-gray-900 dark:text-white">{item.code}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
                          {item.attempts && (
                            <div className="mt-1">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 max-w-[80px] h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                                    style={{ width: `${(item.attempts / MAX_ATTEMPTS) * 100}%` }}
                                  ></div>
                                </div>
                                <p className="text-xs text-gray-500">
                                  Used: {item.attempts}/{MAX_ATTEMPTS}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                          item.status === 'valid' 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                            : item.status === 'exhausted'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}>
                          {item.status === 'valid' ? 'Verified ✓' : item.status === 'exhausted' ? 'Expired ✗' : 'Invalid ✗'}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                    No recent verifications
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Anti-Counterfeit",
              desc: "Advanced security features to ensure product authenticity"
            },
            {
              title: "Limited Verifications",
              desc: `Each code can be verified maximum ${MAX_ATTEMPTS} times for security`
            },
            {
              title: "Instant Results",
              desc: "Get immediate verification results in seconds"
            }
          ].map((feature, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-gray-700 dark:to-gray-700 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {index === 0 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
                  {index === 1 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />}
                  {index === 2 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />}
                </svg>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}